// Versions (DES1 `app-parcels-versions`). A version is a (revision, snapshot) with a cause. The
// builder's origin and the publisher are separate facts; "Built with" reads the registry label.
export type VersionOrigin =
  | { kind: 'builder'; model: string | null; forPerson?: string }
  | { kind: 'agent'; label: string; runId?: string }
  | { kind: 'terminal'; label: string; runId?: string };

export type AppVersionRow = {
  revision: number;
  /** The unpublished working copy, shown first. */
  isDraft: boolean;
  summary: string;
  at: string;
  basedOn?: number;
  origin: VersionOrigin;
  /** Set once published; the live version is the one with `live`. */
  published?: { by: string; at: string } | null;
  live: boolean;
  preview?: { light?: string; dark?: string };
};

/** Draft first, then newest to oldest. */
export function orderVersions(rows: readonly AppVersionRow[]): AppVersionRow[] {
  return [...rows].sort((a, b) => {
    if (a.isDraft !== b.isDraft) return a.isDraft ? -1 : 1;
    return b.revision - a.revision;
  });
}

export type VersionActions = { publish: boolean; restore: boolean; openRun: boolean };

/** What each row offers. Restore changes code only and is for editors; the live version has none. */
export function actionsFor(
  row: AppVersionRow,
  canEdit: boolean,
  canPublish: boolean,
): VersionActions {
  return {
    publish: row.isDraft && canPublish,
    restore: canEdit && !row.isDraft && !row.live,
    openRun: Boolean(
      (row.origin.kind === 'agent' || row.origin.kind === 'terminal') && row.origin.runId,
    ),
  };
}

/** The pair the compare slider shows: the draft against the live version, else newest against previous. */
export function comparePair(
  rows: readonly AppVersionRow[],
): { from: AppVersionRow; to: AppVersionRow } | null {
  const ordered = orderVersions(rows);
  const to = ordered[0];
  if (!to) return null;
  const from = ordered.find((row) => row.live && row !== to) ?? ordered.find((row) => row !== to);
  return from ? { from, to } : null;
}

/** Slider position is a percentage, kept in range. */
export const clampPercent = (value: number) =>
  Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 50;
